import { Suspense, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useCompanyStore } from "@/common/states/store/Company";
import {
  getChargePaged,
  IPartnerInvoiceCharge,
} from "@/common/services/partner-charge.api";
import { AuthContext } from "@/common/components/auth/authContext/AuthContext";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import {
  TableFilters,
  TableFiltersState,
} from "@/common/components/ui/table/TableFilters";
import Loading from "@/common/components/ui/loading/Loading";
import GenericTable from "@/common/components/ui/table/GenericTable";
import TableFooter from "@/common/components/ui/table/TableFooter";
import { CHARGE_STATUS, CHARGE_TYPE } from "@/types/charge.type";
import { formatDate, formatDateAndTime } from "@/common/helpers/date.helpers";
import { numberToCurrency } from "@/common/helpers/currency.helpers";

const STATUS_LABELS: Record<string, string> = {
  [CHARGE_STATUS.PENDING]: "Pendente",
  [CHARGE_STATUS.OVERDUE]: "Vencida",
  [CHARGE_STATUS.RECEIVED]: "Paga",
  [CHARGE_STATUS.CONFIRMED]: "Confirmada",
  [CHARGE_STATUS.REMOVED]: "Removida",
  [CHARGE_STATUS.RENEGOTIATED]: "Renegociada",
  [CHARGE_STATUS.RENEGOTIATION]: "Em renegociação",
  [CHARGE_STATUS.NOT_CHARGED]: "Não cobrada",
};

const STATUS_COLORS: Record<string, string> = {
  [CHARGE_STATUS.PENDING]: "bg-yellow-100 text-yellow-800",
  [CHARGE_STATUS.OVERDUE]: "bg-red-100 text-red-800",
  [CHARGE_STATUS.RECEIVED]: "bg-green-100 text-green-800",
  [CHARGE_STATUS.CONFIRMED]: "bg-emerald-100 text-emerald-800",
  [CHARGE_STATUS.REMOVED]: "bg-gray-100 text-gray-700",
  [CHARGE_STATUS.RENEGOTIATED]: "bg-blue-100 text-blue-800",
  [CHARGE_STATUS.RENEGOTIATION]: "bg-indigo-100 text-indigo-800",
  [CHARGE_STATUS.NOT_CHARGED]: "bg-slate-100 text-slate-700",
};

const CHARGE_TYPE_LABELS: Record<string, string> = {
  [CHARGE_TYPE.MAINTENANCE]: "Manutenção",
  [CHARGE_TYPE.PREPARATION]: "Preparação",
  [CHARGE_TYPE.REPREPARATION]: "Repreparação",
  [CHARGE_TYPE.CONTRACTFINE]: "Multa contratual",
  [CHARGE_TYPE.TRAFFICFINE]: "Multa",
  [CHARGE_TYPE.MANAGEMENTTRAFFICFINES]: "Gestão de multa",
  [CHARGE_TYPE.WEEKLY]: "Semanal",
  [CHARGE_TYPE.RECISION]: "Rescisão",
  [CHARGE_TYPE.REACTIVATION]: "Reativação",
};

const HEADERS: Record<string, string> = {
  partnerName: "Parceiro",
  externalId: "Fatura",
  notes: "Descrição",
  chargeType: "Tipo",
  amount: "Valor",
  dueDate: "Vencimento",
  status: "Status",
  updatedAt: "Última Atualização",
};

const SORT_FIELDS: Record<string, string> = {
  partnerName: "clientId",
  externalId: "externalId",
  notes: "notes",
  chargeType: "chargeType",
  amount: "amount",
  dueDate: "dueDate",
  status: "status",
  updatedAt: "updatedAt",
};

const getInvoicePathId = (externalId: string) =>
  externalId.replace(/^pay_/, "").trim();

const getInvoiceUrl = (externalId: string) => {
  const invoicePathId = getInvoicePathId(externalId);

  if (!invoicePathId) {
    return "";
  }

  return `https://www.asaas.com/i/${invoicePathId}`;
};

const mapDataForTable = (
  data: IPartnerInvoiceCharge[],
): Record<string, unknown>[] => {
  return data.map((item) => ({
    ...item,
    partnerName: item.partnerName || item.partneName || "-",
    _original: item,
  })) as unknown as Record<string, unknown>[];
};

export default function TablePartnerCharge() {
  useContext(AuthContext);
  const { notifyError } = useToastContext();
  const company = useCompanyStore();

  const [filters, setFilters] = useState<TableFiltersState>({
    search: "",
    status: [],
    filterColumn: "",
    orderBy: "-updatedAt",
    itemsPerPage: "10",
  });

  const [rows, setRows] = useState<IPartnerInvoiceCharge[]>([]);
  const [page, setPage] = useState<number>(1);
  const [data, setData] = useState({ itemsPerPage: 10, totalItems: 0 });
  const [isLoading, setIsLoading] = useState(false);

  const actions = useMemo(
    () => [
      {
        label: "Visualizar Fatura",
        action: (data: unknown) => {
          const item = data as IPartnerInvoiceCharge;
          const invoiceUrl = getInvoiceUrl(String(item.externalId || ""));

          if (!invoiceUrl) {
            notifyError("Link da fatura indisponível para esta charge.");
            return;
          }

          window.open(invoiceUrl, "_blank", "noopener,noreferrer");
        },
        permission: "view:finance",
      },
    ],
    [notifyError],
  );

  const loadCharges = useCallback(async () => {
    setIsLoading(true);
    const result = await getChargePaged({
      ...filters,
      page,
      itemsPerPage: Number(filters.itemsPerPage) || data.itemsPerPage,
    });

    if (!result.ok) {
      notifyError("Não foi possível carregar as cobranças dos parceiros.");
      setIsLoading(false);
      return;
    }

    setData((prevData) => ({
      itemsPerPage: prevData.itemsPerPage,
      totalItems: result.totalItems,
    }));
    setRows(result.data as IPartnerInvoiceCharge[]);
    setIsLoading(false);
  }, [filters, page, data.itemsPerPage, notifyError]);

  useEffect(() => {
    loadCharges();
  }, [loadCharges, company]);

  const columnRenderers: Record<
    string,
    (val: unknown, row: unknown) => JSX.Element | string
  > = {
    partnerName: (value: unknown) => String(value || "-"),
    externalId: (value: unknown) => {
      const invoiceUrl = getInvoiceUrl(String(value || ""));

      if (!invoiceUrl) {
        return "-";
      }

      return (
        <a
          href={invoiceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-bluePrimary hover:underline font-medium"
        >
          Link
        </a>
      );
    },
    notes: (value: unknown) => String(value || "-"),
    chargeType: (value: unknown) =>
      CHARGE_TYPE_LABELS[String(value)] || String(value || "-"),
    amount: (value: unknown) => numberToCurrency(Number(value || 0)),
    dueDate: (value: unknown) => formatDate(value),
    status: (value: unknown) => (
      <span
        className={`px-2 py-1 rounded text-xs font-semibold ${
          STATUS_COLORS[String(value)] || "bg-gray-100 text-gray-700"
        }`}
      >
        {STATUS_LABELS[String(value)] || String(value)}
      </span>
    ),
    updatedAt: (value: unknown) => formatDateAndTime(value),
  };

  const handleFilterChange = (newFilters: Partial<TableFiltersState>) => {
    setPage(1);
    setFilters((prevFilters) => ({ ...prevFilters, ...newFilters }));
  };

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          dashboardHeader="Cobranças de Parceiros"
          placeholder="Digite o nome do parceiro, ID ou descrição da fatura"
          statusOptions={STATUS_LABELS}
          onSearch={handleFilterChange}
          tableData={rows as unknown as Record<string, unknown>[]}
        />

        <GenericTable
          entity="partner-charge"
          headers={HEADERS}
          data={mapDataForTable(rows)}
          actions={actions}
          renders={columnRenderers}
          orderBy={filters.orderBy}
          sortFields={SORT_FIELDS}
          setFilters={({ orderBy }) => setFilters((f) => ({ ...f, orderBy }))}
        />

        {isLoading && <Loading />}

        <TableFooter
          page={page || 0}
          totalItems={data.totalItems || 0}
          itemsPerPage={+(filters.itemsPerPage ?? 10)}
          goToPage={setPage}
        />
      </div>
    </Suspense>
  );
}
